import { isSameWeek, format } from "date-fns";
import { parseISO } from "date-fns";

export * from "./names";
export * from "./profile-pictures";
export * from "./sidebar";

export const formatDuration = (minutes: number): string => {
  // Round up to the nearest minute if there's any time at all
  const roundedMinutes = minutes > 0 ? Math.ceil(minutes) : 0;
  
  if (roundedMinutes === 0) {
    return "0 Minutes";
  }
  
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes} ${remainingMinutes === 1 ? 'Minute' : 'Minutes'}`;
  }
  
  const hourText = hours === 1 ? 'Hour' : 'Hours';
  const minuteText = remainingMinutes === 1 ? 'Minute' : 'Minutes';
  
  if (remainingMinutes === 0) {
    return `${hours} ${hourText}`;
  }
  
  return `${hours} ${hourText} ${remainingMinutes} ${minuteText}`;
};

/**
 * Check if a student's birthday falls within the current week
 * @param dateOfBirth - The student's date of birth (string)
 * @returns 'today' | 'week' | null - Returns 'today' if it's their birthday today, 'week' if it's their birthday week, null otherwise
 */
export const getStudentBirthdayStatus = (dateOfBirth: string | null): 'today' | 'week' | null => {
  if (!dateOfBirth) return null;
  
  try {
    const todayDate = new Date();
    
    // Parse the date string more carefully to avoid timezone issues
    // dateOfBirth is in format "YYYY-MM-DD"
    const [, month, day] = dateOfBirth.split('-').map(Number);
    
    // Create birthday this year using the same year as today
    // Use local date components to avoid timezone issues
    const todayYear = todayDate.getFullYear();
    const todayMonth = todayDate.getMonth();
    const todayDay = todayDate.getDate();
    
    // month is 1-indexed in the string, but 0-indexed in Date constructor
    const birthdayMonth = month - 1;
    const birthdayDay = day;
    
    const birthdayThisYear = new Date(todayYear, birthdayMonth, birthdayDay);
    
    // Check if it's their birthday today
    if (todayMonth === birthdayMonth && todayDay === birthdayDay) {
      return 'today';
    }
    
    // Check if it's their birthday week
    if (isSameWeek(todayDate, birthdayThisYear, { weekStartsOn: 0 })) {
      return 'week';
    }
    
    return null;
  } catch (error) {
    console.error('Error in getStudentBirthdayStatus:', error);
    return null;
  }
};

/**
 * Debug function to test birthday status for a specific date
 * @param testDate - The date to test in YYYY-MM-DD format
 * @returns 'today' | 'week' | null
 */
export const testBirthdayStatus = (testDate: string) => {
  console.log(`Testing birthday status for date: ${testDate}`);
  const result = getStudentBirthdayStatus(testDate);
  console.log(`Result: ${result}`);
  return result;
};

/**
 * Check if a student's birthday falls within the current week (legacy function for backward compatibility)
 * @param dateOfBirth - The student's date of birth (string)
 * @returns boolean - True if it's the student's birthday week
 */
export const isStudentBirthdayWeek = (dateOfBirth: string | null): boolean => {
  const status = getStudentBirthdayStatus(dateOfBirth);
  return status === 'today' || status === 'week';
};

/**
 * Get the appropriate end time for a class based on enrollment type and day
 * @param classData - The class data with schedule
 * @param dayOfWeek - The day of the week (e.g., 'monday', 'tuesday')
 * @param enrollmentType - The enrollment type (full or half day)
 * @returns string - The end time in HH:MM format
 */
export const getClassEndTime = (
  classData: {
    schedule?: {
      [key: string]: {
        end?: string;
        halfEnd?: string | null;
        supportsHalf?: boolean;
        hasSchool?: boolean;
      };
    };
  },
  dayOfWeek: string,
  enrollmentType: 'full' | 'half' = 'full'
): string | null => {
  if (!classData?.schedule) return null;
  
  const daySchedule = classData.schedule[dayOfWeek.toLowerCase()];
  if (!daySchedule?.hasSchool) return null;
  
  // If enrollment is half-day and this day supports half-day, use half-day end time
  if (enrollmentType === 'half' && daySchedule.supportsHalf && daySchedule.halfEnd) {
    return daySchedule.halfEnd;
  }
  
  // Otherwise use full-day end time
  return daySchedule.end || null;
};

/**
 * Extract the date portion (YYYY-MM-DD) from an ISO timestamp string using local timezone
 * This prevents timezone issues where dates can be off by one day
 * @param isoString - ISO timestamp string (e.g., "2024-10-20T14:30:00Z")
 * @returns string - Date in YYYY-MM-DD format based on local timezone
 */
export const getLocalDateString = (isoString: string): string => {
  return format(parseISO(isoString), 'yyyy-MM-dd');
};

/**
 * Format an ISO timestamp string for display
 * @param isoString - ISO timestamp string (e.g., "2024-10-20T14:30:00Z")
 * @param timeFormat - Format string for time (default: "h:mm a")
 * @param dateFormat - Format string for date (default: "M/d/yyyy")
 * @returns string - Formatted string like "2:30 PM (10/20/2024)"
 */
export const formatISOTimestamp = (
  isoString: string,
  timeFormat: string = "h:mm a",
  dateFormat: string = "M/d/yyyy"
): string => {
  const date = parseISO(isoString);
  return `${format(date, timeFormat)} (${format(date, dateFormat)})`;
};

// Re-export schedule formatting utilities
export { formatScheduleDisplay, getEnrollmentTypeDisplay } from './schedule-formatting';

// Make test function available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).testBirthdayStatus = testBirthdayStatus;
}